// filepath: /Users/minnies/kalpa-connect-dashboard/src/hooks/use-broker-connections.ts
import React, { createContext, useContext, useState, ReactNode, useCallback, useEffect } from 'react';

interface BrokerConnectionsState {
  tastytradeConnected: boolean;
  // Add other brokers here as needed, e.g.,
  // schwabConnected: boolean;
  // alpacaConnected: boolean;
}

interface BrokerConnectionsContextType extends BrokerConnectionsState {
  setBrokerConnected: (broker: keyof BrokerConnectionsState, status: boolean) => void;
}

const BrokerConnectionsContext = createContext<BrokerConnectionsContextType | undefined>(undefined);

export const BrokerConnectionsProvider: React.FC<{ children: ReactNode }> = ({ children }) => {
  const [connections, setConnections] = useState<BrokerConnectionsState>(() => {
    // Initialize state from localStorage if available
    const storedTastytradeConnected = localStorage.getItem('tastytradeConnected') === 'true';
    return {
      tastytradeConnected: storedTastytradeConnected,
      // Initialize other brokers here
    };
  });

  const setBrokerConnected = useCallback((broker: keyof BrokerConnectionsState, status: boolean) => {
    setConnections(prevConnections => {
      const newConnections = {
        ...prevConnections,
        [broker]: status,
      };
      // Persist to localStorage
      if (broker === 'tastytradeConnected') {
        localStorage.setItem('tastytradeConnected', String(status));
      }
      // Add similar localStorage logic for other brokers if they also need persistence
      return newConnections;
    });
  }, []);

  // Effect to update tastytradeConnected status if localStorage token exists
  useEffect(() => {
    const token = localStorage.getItem('tastytrade_access_token');
    if (token && !connections.tastytradeConnected) {
      setBrokerConnected('tastytradeConnected', true);
    }
    if (!token && connections.tastytradeConnected) {
      // This case might occur if token is manually cleared or expires
      // and the app is reloaded without going through callback.
      setBrokerConnected('tastytradeConnected', false);
    }
  }, []); // Runs once on mount

  return (
    <BrokerConnectionsContext.Provider value={{ ...connections, setBrokerConnected }}>
      {children}
    </BrokerConnectionsContext.Provider>
  );
};

export const useBrokerConnections = (): BrokerConnectionsContextType => {
  const context = useContext(BrokerConnectionsContext);
  if (context === undefined) {
    throw new Error('useBrokerConnections must be used within a BrokerConnectionsProvider');
  }
  return context;
};
